import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContextType, AuthState, LoginCredentials, RegisterCredentials, User } from '../types/auth';

interface ApiResponse {
  error?: string;
  message?: string;
  user?: User;
}

// Initialize with loading state to prevent flash
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading=true to prevent flash
  error: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);
  const navigate = useNavigate();

  // Add a local storage key for session tracking
  const LOCAL_SESSION_KEY = 'auth_session_valid';

  // Add isLoggingOut ref to prevent validation during logout
  const isLoggingOut = React.useRef(false);

  // Add session validation on mount
  useEffect(() => {
    const validateSession = async () => {
      const startTime = performance.now();
      
      // Skip validation if logging out
      if (isLoggingOut.current) {
        return;
      }

      // Quick check: if localStorage indicates no valid session, skip API call
      const hasLocalSession = localStorage.getItem(LOCAL_SESSION_KEY) === 'true';
      if (!hasLocalSession) {
        setState(prev => ({ ...prev, user: null, isAuthenticated: false, isLoading: false }));
        return;
      }
      
      try {
        const fetchStart = performance.now();
        const response = await fetch('/api/auth/validate', {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        const fetchEnd = performance.now();

        if (!response.ok) {
          console.log('Session validation failed:', response.status);
          localStorage.setItem(LOCAL_SESSION_KEY, 'false');
          setState(prev => ({ ...prev, user: null, isAuthenticated: false, isLoading: false }));
          return;
        }

        const data = await response.json() as { authenticated: boolean; user?: User };
        
        if (data.authenticated && data.user) {
          localStorage.setItem(LOCAL_SESSION_KEY, 'true');
          setState(prev => ({
            ...prev,
            user: data.user || null,
            isAuthenticated: true,
            isLoading: false
          }));
        } else {
          localStorage.setItem(LOCAL_SESSION_KEY, 'false');
          setState(prev => ({
            ...prev,
            user: null,
            isAuthenticated: false,
            isLoading: false
          }));
        }
      } catch (error) {
        console.error('Session validation error:', error);
        localStorage.setItem(LOCAL_SESSION_KEY, 'false');
        setState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'Session validation failed'
        }));
      }
    };

    validateSession();
  }, []);

  const setLoading = (loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading, error: null }));
  };

  const setError = (error: string) => {
    setState(prev => ({ ...prev, error, isLoading: false }));
  };

  const setUser = (user: User | null) => {
    console.log('Setting user:', user);
    setState(prev => ({
      ...prev,
      user,
      isAuthenticated: !!user,
      isLoading: false,
      error: null,
    }));
  };

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
        credentials: 'include', // Important for cookies
      });

      const data = await response.json() as ApiResponse;

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Set local session as valid
      localStorage.setItem(LOCAL_SESSION_KEY, 'true');
      
      // Update auth state with user data
      setUser(data.user || null);
      
      // After successful login, navigate to the main interface
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed');
    }
  }, [navigate]);

  const register = useCallback(async (credentials: RegisterCredentials) => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const data = await response.json() as ApiResponse;

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: null,
        user: null,
        isAuthenticated: false,
        successMessage: data.message || 'Please check your email to verify your account'
      }));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Registration failed');
    }
  }, []);

  const verifyEmail = useCallback(async (token: string) => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await response.json() as ApiResponse;

      if (!response.ok) {
        throw new Error(data.error || 'Email verification failed');
      }

      setUser(data.user || null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Email verification failed');
    }
  }, []);

  const verifyToken = useCallback(async (email: string, password: string, token: string) => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/verify-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, token }),
        credentials: 'include',
      });

      const data = await response.json() as ApiResponse;

      if (!response.ok) {
        throw new Error(data.error || 'Token verification failed');
      }

      // Set local session as valid
      localStorage.setItem(LOCAL_SESSION_KEY, 'true');
      
      // Update auth state with user data
      setUser(data.user || null);
      
      // Navigate to main interface after successful verification
      navigate('/', { replace: true });
      
      return data.user;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Token verification failed');
      throw error;
    }
  }, [navigate]);

  const resendVerification = useCallback(async (email: string) => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json() as ApiResponse;

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
        successMessage: 'Verification email sent! Please check your inbox.'
      }));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to resend verification email');
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json() as ApiResponse;

      if (!response.ok) {
        throw new Error(data.error || 'Password reset request failed');
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
        successMessage: 'Password reset instructions sent! Please check your email.'
      }));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Password reset failed');
    }
  }, []);

  const clearLocalState = useCallback(() => {
    // Clear all auth-related cookies
    const cookiesToClear = ['session', 'authenticated', 'token', 'auth'];
    const domains = [window.location.hostname, `.${window.location.hostname}`];
    const paths = ['/', '/api'];

    cookiesToClear.forEach(cookieName => {
      paths.forEach(path => {
        domains.forEach(domain => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
        });
      });
    });

    // Clear local storage
    localStorage.removeItem(LOCAL_SESSION_KEY);
    
    // Clear auth state
    setState(prev => ({
      ...prev,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    }));
  }, []);

  const logout = useCallback(async () => {
    try {
      // Set logging out flag immediately to block validation
      isLoggingOut.current = true;
      localStorage.setItem('logging_out', 'true');
      
      // Set loading state
      setState(prev => ({ ...prev, isLoading: true }));

      // Attempt server-side session invalidation first
      const maxRetries = 2;
      let retryCount = 0;
      let success = false;

      while (retryCount < maxRetries && !success) {
        try {
          const response = await fetch('/api/auth/session/invalidate', { 
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });
          
          if (response.ok) {
            const data = await response.json() as { success: boolean };
            if (data.success) {
              success = true;
              break;
            }
          }
        } catch (error) {
          console.warn(`Logout attempt ${retryCount + 1} failed:`, error);
        }
        retryCount++;
        // Small delay between retries
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Clear client state regardless of server response
      clearLocalState();

      // Additional cookie clearing for all possible paths and domains
      const cookiesToClear = ['session', 'authenticated', 'token', 'auth', 'auth_session', 'csrf_token'];
      const domains = [
        window.location.hostname,
        `.${window.location.hostname}`,
        window.location.hostname.split('.').slice(1).join('.'),
        ''
      ];
      const paths = ['/', '/api', '/api/auth', ''];

      cookiesToClear.forEach(cookieName => {
        paths.forEach(path => {
          domains.forEach(domain => {
            const cookieStr = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domain ? `; domain=${domain}` : ''}; Secure; SameSite=Lax`;
            document.cookie = cookieStr;
          });
        });
      });

      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Reset auth state
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });

      // Force a clean reload to login page
      window.location.replace('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, force reload to login
      window.location.replace('/login');
    }
  }, [clearLocalState]);

  const value = {
    ...state,
    login,
    register,
    logout,
    resetPassword,
    verifyEmail,
    verifyToken,
    resendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 